/* eslint-disable react-hooks/refs -- deliberate render-time ref cache;
   see the "use no memo" rationale inside useStableValue */
import { useRef } from "react";

/**
 * Keep the previous reference when a newly computed value is content-equal.
 *
 * `useMemo` can only key on dependency identity, so a memo that derives
 * from a frequently-replaced store object (e.g. `logDetails` during a
 * directory sync) returns a fresh reference on every flush even when its
 * content is unchanged — cascading identity churn into everything keyed
 * on it. This hook cuts that cascade at the source: callers compute the
 * candidate value as usual and equality decides whether downstream
 * consumers see a new reference.
 *
 * `isEqual` is only consulted when the identity actually changed.
 */
export function useStableValue<T>(
  value: T,
  isEqual: (a: T, b: T) => boolean
): T {
  // Deliberate render-time ref cache — identity stabilization can't be
  // expressed with useMemo or compiler memoization (both re-derive output
  // identity from input identity). Safe under concurrent rendering
  // because reuse is gated on content equality: whichever reference a
  // discarded or committed render leaves behind, it is content-equal to
  // the latest value.
  "use no memo";
  const ref = useRef(value);
  if (ref.current !== value && !isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}
